import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Checkbox from '../../Checkbox';
import CurrencyField from '../../CurrencyField';
import PercentageField from '../../PercentageField';
import TextField from '../../TextField';
import ToggleButtonGroup from '../../ToggleButtonGroup';
import Icon from '../../Icon';
import translations from './translations';

const Adjustment = ({
  adjustment,
  onChange,
  register: shouldRegister,
  inline,
  isEditing,
  readOnly,
}) => {
  const intl = useIntl();
  const onChangeRef = useRef(onChange);
  const isPercentage = !!adjustment?.isPercentage;
  const isDiscount = !!adjustment?.isDiscount;

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  /**
   * Handle the on change event.
   * @param {String} name
   */
  const handleOnChange = useCallback(
    name => (event, newValue) => {
      if (onChangeRef.current) {
        onChangeRef.current(name, event, newValue);
      }
    },
    [],
  );

  const handleChangeType = (event, newValue) => {
    handleOnChange('isPercentage')(event, newValue);
    handleOnChange('amount')(null, '');
  };

  const handleChangeIsDiscount = (ev, value) => {
    handleOnChange('isDiscount')(ev, value);
  };

  return (
    <>
      <TextField
        name="description"
        label={intl.formatMessage(translations.fields.description.label)}
        value={adjustment?.description ?? ''}
        onChange={handleOnChange('description')}
        register={shouldRegister}
        inline={inline}
        isEditing={isEditing}
        readOnly={readOnly}
      />

      <ToggleButtonGroup
        value={isPercentage}
        name="isPercentage"
        margin="none"
        options={[
          {
            label: <Icon icon={['fal', 'euro-sign']} size="small" />,
            value: false,
          },
          {
            label: <Icon icon={['fal', 'percentage']} size="small" />,
            value: true,
          },
        ]}
        onChange={handleChangeType}
        register={shouldRegister}
        inline={inline}
        isEditing={isEditing}
        readOnly={readOnly}
      />

      {adjustment.isPercentage && (
        <PercentageField
          name="amount"
          label={intl.formatMessage(translations.fields.percentage.label)}
          value={adjustment?.amount ?? ''}
          onChange={handleOnChange('amount')}
          register={shouldRegister}
          inline={inline}
          isEditing={isEditing}
          readOnly={readOnly}
        />
      )}

      {!adjustment.isPercentage && (
        <CurrencyField
          name="amount"
          label={intl.formatMessage(translations.fields.amount.label)}
          value={adjustment?.amount ?? null}
          onChange={handleOnChange('amount')}
          register={shouldRegister}
          inline={inline}
          isEditing={isEditing}
          readOnly={readOnly}
        />
      )}

      <Checkbox
        name="isDiscount"
        label={intl.formatMessage(translations.fields.isDiscount.label)}
        checked={isDiscount}
        onChange={handleChangeIsDiscount}
        register={shouldRegister}
        inline={inline}
        isEditing={isEditing}
        readOnly={readOnly}
      />
    </>
  );
};

Adjustment.propTypes = {
  adjustment: PropTypes.object,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  register: PropTypes.bool,
};

Adjustment.defaultProps = {
  adjustment: {},
  inline: false,
  isEditing: false,
  onChange: undefined,
  readOnly: false,
  register: true,
};

export default Adjustment;
